// League tab: the league's home. A title card carrying the league name and a
// row of circular section buttons, a segmented control for the two standings
// views, then the selected section below.
//
// MyLeague/LeagueMembers/LeagueRules keep their own interiors (each is
// self-contained via useLeague()/useDraftContext(), with no page chrome of
// their own since LeagueLayout supplies that). This file owns the chrome: the
// single full-height container, the one background colour, and the title. The
// sections used to set their own min-h-screen inside this one, which made
// every section two viewports tall, and they disagreed on the background so
// the page changed tone between sections.
//
// Cards run edge to edge below 640px and square off their corners there. A
// phone has no room to spend on a gutter down both sides of a table whose
// columns are already tight, and the inset made the rows read as a widget
// rather than as the page. From sm up they take the centred max-w-4xl column
// back, since full-bleed rows on a laptop stretch to an unreadable width.
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, BookOpen, Zap, Calculator, Newspaper } from "lucide-react";
import { useLeague } from "../context/LeagueContext";
import { useTeams } from "../hooks/useTeams";
import { useSeasonConfig } from "../hooks/useSeasonConfig";
import { useActivityUnread } from "../hooks/useActivityUnread";
import ScoringSystemModal from "../components/ScoringSystemModal";
import PlayoffPicture from "../components/league/PlayoffPicture";
import RecentMovesWidget from "../components/RecentMovesWidget";
import { getDisplayData, getCurrentWeekNumber } from "../utils/leagueUtils";
import { isOffSeason, FIRST_PLAYOFF_WEEK } from "../utils/season";
import MyLeague from "./MyLeague";
import LeagueMembers from "./LeagueMembers";
import LeagueRules from "./LeagueRules";
import { usePageTour } from "../hooks/usePageTour";

// The two views of the same table. They sit in a segmented control rather
// than in the icon row because they are two readings of one thing: where
// everyone is now, and where that puts them in the bracket.
const VIEWS = [
  { key: "standings", label: "Standings" },
  { key: "playoffs", label: "Playoffs" },
];

// Everything else is a destination that takes over the content area. One
// compact pill per destination — icon plus a single-line label — rather than
// the large circle-and-two-line buttons this replaced. Those cost 56px of
// height for the icon alone before the label even started, so four of them
// plus Scoring and Draft Room ran wider than a phone screen. A pill row reads
// the same way a filter chip row does: scannable at a glance, and it scrolls
// instead of forcing a taller card.
const SECTIONS = [
  { key: "activity", label: "Recent Activity", icon: Newspaper },
  { key: "members", label: "League Members", icon: Users },
  { key: "rules", label: "League Settings", icon: BookOpen },
];

function SectionButton({ label, icon: Icon, active, onClick, badge = 0 }) {
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 flex items-center gap-1.5 rounded-full px-3 py-2 transition-colors"
      style={{
        backgroundColor: active ? "var(--color-brand)" : "var(--color-bg-page)",
        color: active ? "var(--color-bg-card)" : "var(--color-text-muted)",
      }}
    >
      <Icon size={15} strokeWidth={2} />
      <span className="text-[12.5px] font-semibold whitespace-nowrap">{label}</span>
      {/* Inline rather than floated over the corner: the pill row scrolls
          sideways, and its overflow would clip anything outside a pill. */}
      {badge > 0 && (
        <span
          aria-label={`${badge > 9 ? "More than 9" : badge} new`}
          className="flex items-center justify-center rounded-full text-[11px] font-bold"
          style={{ minWidth: 18, height: 18, padding: "0 5px", backgroundColor: "var(--color-danger-fill)", color: "var(--color-bg-card)" }}
        >
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}

// A pill on a grey track. The track stays visible with no pill on it while a
// destination from the icon row is open, so the control still reads as the
// way back to the table rather than claiming one of its views is showing.
function ViewToggle({ view, onSelect }) {
  return (
    <div
      className="flex rounded-full p-1"
      style={{ backgroundColor: "var(--color-border)" }}
      role="tablist"
    >
      {VIEWS.map(({ key, label }) => {
        const active = view === key;
        return (
          <button
            key={key}
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(key)}
            className="flex-1 rounded-full py-2 text-[13px] font-semibold transition-colors"
            style={{
              backgroundColor: active ? "var(--color-bg-card)" : "transparent",
              color: active ? "var(--color-text-strong)" : "var(--color-text-muted)",
              boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function LeagueTab() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { isDraftComplete, leagueData, members, currentWeek, currentMemberId, isDemo } = useLeague();
  usePageTour("league", { demo: isDemo, nextLabel: "Next: free agency", nextTo: `/${leagueId}/teams` });
  const { teams: allTeams } = useTeams();
  const { config: seasonConfig } = useSeasonConfig();

  // The always-current week, distinct from the selector inside MyLeague:
  // that one can page back to a past week's snapshot, but this banner always
  // names the live week so it reads the same regardless of what the table
  // below is showing.
  const currentWeekNum = getCurrentWeekNumber(currentWeek);
  const weekLabel = isOffSeason(seasonConfig?.currentWeek)
    ? "Preseason"
    : currentWeekNum >= FIRST_PLAYOFF_WEEK
      ? `Playoff Week ${currentWeekNum}`
      : `Week ${currentWeekNum}`;
  // Which of the two table views is selected, and which icon-row destination
  // is open over it. Kept apart so closing a destination returns to whichever
  // view was showing rather than resetting to standings.
  const [view, setView] = useState("standings");
  const [section, setSection] = useState(null);
  const [showScoring, setShowScoring] = useState(false);

  const { count: unreadActivity, markSeen: markActivitySeen } = useActivityUnread({
    leagueId: leagueData?.id,
    // No member in the demo: the unread marker is a write to league_members.
    memberId: isDemo ? null : currentMemberId,
    members,
  });
  // Opening the section marks everything seen, and so does a refresh that
  // finds something new while it is already open.
  useEffect(() => {
    if (section === "activity" && unreadActivity > 0) markActivitySeen();
  }, [section, unreadActivity, markActivitySeen]);

  // Seeded from the same sort the standings table uses, so the two tabs can
  // never disagree about who is above the line. LeagueContext members carry no
  // weeklyPoints, so a tie on season points falls to team name here rather than
  // to the week, which only matters while two managers are exactly level.
  const standings = useMemo(
    () =>
      getDisplayData({
        viewMode: "current",
        members: (members || []).map((m) => ({
          ...m,
          teamName: m.team_name || "Unnamed Team",
        })),
        weeklyStandings: {},
        selectedWeek: null,
      }),
    [members]
  );

  const selectView = (key) => {
    setSection(null);
    setView(key);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg-page)" }}>
      <div className="max-w-4xl mx-auto sm:px-6 pt-3 sm:pt-4">
        <div
          className="border-y sm:border sm:rounded-2xl shadow-sm px-4 pt-4 pb-3"
          style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
        >
          <div className="flex items-center justify-between gap-2">
            <h1
              className="text-base font-bold uppercase tracking-wide truncate"
              style={{ color: "var(--color-text-strong)" }}
            >
              {leagueData?.name || "League"}
            </h1>
            <span className="text-base font-semibold flex-shrink-0" style={{ color: "var(--color-text-muted)" }}>
              {weekLabel}
            </span>
          </div>

          <div className="h-px my-3" style={{ backgroundColor: "var(--color-border)" }} />

          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-tour="league-pills">
            {SECTIONS.map(({ key, label, icon }) => (
              <SectionButton
                key={key}
                label={label}
                icon={icon}
                active={section === key}
                badge={key === "activity" ? unreadActivity : 0}
                onClick={() => setSection(section === key ? null : key)}
              />
            ))}

            {/* Scoring is a modal rather than a section: it is reference
                material you check mid-decision, not somewhere you navigate to
                and come back from. It sits here as well as on the standings
                because a manager works out what the numbers mean while looking
                at them. */}
            <SectionButton
              label="Scoring and Rules"
              icon={Calculator}
              active={false}
              onClick={() => setShowScoring(true)}
            />

            {/* The draft board stays reachable all season: it is the league's
                permanent record of the draft, which is why draft-room is a
                route of its own rather than a section here. */}
            {isDraftComplete && (
              <SectionButton
                label="Draft Room"
                icon={Zap}
                active={false}
                onClick={() => navigate(`/${leagueId}/draft-room`)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Its own card rather than the lid of the table's: the week label and
          the week selector sit between the two, so a shared sheet would break
          open anyway. */}
      <div className="max-w-4xl mx-auto sm:px-6 mt-3 sm:mt-4">
        <div
          className="border-y sm:border sm:rounded-2xl px-4 py-3"
          style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
        >
          <ViewToggle view={section ? null : view} onSelect={selectView} />
        </div>
      </div>

      {section === "activity" && (
        <RecentMovesWidget
          leagueId={leagueId}
          memberId={currentMemberId}
          members={members}
          allTeams={allTeams}
          week={getCurrentWeekNumber(currentWeek)}
          readOnly={isDemo}
          alwaysOpen
        />
      )}
      {section === "members" && <LeagueMembers />}
      {section === "rules" && <LeagueRules />}

      {!section && view === "standings" && <MyLeague />}
      {!section && view === "playoffs" && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 pb-28">
          <PlayoffPicture leagueId={leagueId} standings={standings} />
        </div>
      )}

      {showScoring && <ScoringSystemModal onClose={() => setShowScoring(false)} />}
    </div>
  );
}

export default LeagueTab;
